<script lang="ts" setup>
    import { computed } from 'vue';
    import { toKebabCase } from '@/utils/helper';

    import Lucide from '@/base-components/Lucide/Lucide.vue';
    import Tippy from '@/base-components/Tippy/Tippy.vue';
    import * as lucideIcons from "lucide-vue-next";
    export type Icon = keyof typeof lucideIcons;

    type Action =
        | 'view'
        | 'update'
        | 'delete'
        | 'clone'
        | 'regenerate'
        | 'resetPassword'
        | 'reset2FA'
        | 'acknowledge'
        | 'reply'
        | 'addToPosition'
        | 'lock'
        | 'impersonate'
        | 'impersonator'
        | 'leaveImpersonate'
        | 'submitTicket'
        | 'submitDocument'
        | 'remove'
        | 'addPermission'
        | 'removeUser'
        | 'download'
        | 'markAsComplete'
        | 'holdSms'
        | 'resumeSms'
        | 'add'
        | 'download'
        | 'sign';

    type Props = {
        action: Action;
        size?: string;
        animateSpin?: boolean;
        disabled?: boolean;
        isProcessing?: boolean;
        tippy?: boolean;
        tippyTitle?: string;
        customDescription?: string;
    }

    const props = withDefaults(defineProps<Props>(),
    {
        size: '4',
        animateSpin: false,
        disabled: false,
        isProcessing: false,
        tippy: true,
        tippyTitle: 'Action'
    });

    const icon = computed((): { icon: Icon, action: string } => {
        switch (props.action) {
            case 'view':
                return {
                    icon: 'Eye',
                    action: 'View'
                };
            case 'update':
                return {
                    // icon: 'FileSliders',
                    icon: 'FilePenLine',
                    action: 'Update'
                };
            case 'delete':
                return {
                    icon: 'Trash2',
                    action: 'Delete'
                };
            case 'clone':
                return {
                    icon: 'CopyPlus',
                    action: 'Clone'
                };
            case 'regenerate':
                return {
                    icon: 'RotateCw',
                    action: 'Regenerate'
                };
            case 'resetPassword':
                return {
                    icon: 'Lock',
                    action: 'Reset Password'
                };
            case 'reset2FA':
                return {
                    icon: 'Fingerprint',
                    action: 'Reset 2FA'
                };
            case 'acknowledge':
                return {
                    icon: 'ThumbsUp',
                    action: 'Acknowledge'
                };
            case 'reply':
                return {
                    icon: 'Reply',
                    action: 'Reply'
                };
            case 'addToPosition':
                return {
                    icon: 'CirclePlus',
                    action: 'Add to Position'
                };
            case 'lock':
                return {
                    icon: 'Lock',
                    action: 'Lock'
                };
            case 'impersonate':
                return {
                    icon: 'Users',
                    action: 'Impersonate'
                };
            case 'impersonator':
                return {
                    icon: 'Ghost',
                    action: 'Impersonator'
                };
            case 'leaveImpersonate':
                return {
                    icon: 'UserX',
                    action: 'Leave Impersonate'
                };
            case 'submitTicket':
                return {
                    icon: 'Send',
                    action: 'Submit Ticket'
                };
            case 'submitDocument':
                return {
                    icon: 'Send',
                    action: 'Submit Document'
                };
            case 'remove':
                return {
                    icon: 'CircleX',
                    action: 'Remove'
                };
            case 'addPermission':
                return {
                    icon: 'CirclePlus',
                    action: 'Assign Permission'
                };
            case 'removeUser':
                return {
                    icon: 'CircleX',
                    action: 'Remove User'
                };
            case 'download':
                return {
                    icon: 'Download',
                    action: 'Download'
                };
            case 'markAsComplete':
                return {
                    icon: 'SquareCheckBig',
                    action: 'Mark as Complete'
                };
            case 'holdSms':
                return {
                    icon: 'MailX',
                    action: 'Hold SMS'
                };
            case 'resumeSms':
                return {
                    icon: 'MailCheck',
                    action: 'Resume SMS'
                };
            case 'add':
                return {
                    icon: 'CirclePlus',
                    action: 'Add'
                };
            case 'sign':
                return {
                    icon: 'ScanEye',
                    action: 'Review & Sign'
                };
            default:
                return {
                    icon: 'CircleHelp',
                    action: 'Undefined'
                };
        }
    });
</script>

<template>
    <Tippy
        :content="customDescription ? customDescription : `
        <div class='flex flex-col justify-start text-start'>
            <p class='font-medium text-[10px] text-slate-400'>${tippyTitle}:</p>
            <p class='font-medium'>${icon.action}</p>
        </div>
        `"
        :options="{
            theme: 'light',
            allowHTML: true,
        }"
        :disabled="!tippy || props.disabled"
        :data-testid="toKebabCase(icon.action)"
    >
        <Lucide
            v-if="!isProcessing"
            :icon="icon.icon"
            :class="[`size-${props.size} text-slate-400 hover:text-custom-tertiary`,
            {
                'animate-spin cursor-not-allowed': props.animateSpin,
                '!cursor-not-allowed !text-slate-300 opacity-50': props.disabled
            }
        ]"
        />
        <div v-else>
            <svg
                aria-hidden="true"
                class="text-gray-200 animate-spin fill-blue-600 dark:text-gray-600"
                :class="[`size-${props.size}`]"
                fill="none"
                viewBox="0 0 100 101"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="currentColor"
                />
                <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentFill"
                />
            </svg>
        </div>
    </Tippy>
</template>
